// src/pages/GameTerminology.js

import React from 'react';
import './GameTerminology.css';

const GameTerminology = () => {
  return (
    <main className="terminology-page-shell">
      <div className="terminology-background-grid" />
      <div className="terminology-orb terminology-orb--one" />
      <div className="terminology-orb terminology-orb--two" />

      <header className="terminology-hero">
        <p className="terminology-eyebrow">K&amp;M Learning Center</p>
        <h1>
          Learn the language of <span>bingo and gaming.</span>
        </h1>
        <p className="terminology-introduction">
          Explore common terminology used throughout bingo, pull-tab games,
          tickets, boards, and fundraising events.
        </p>
        <div className="terminology-summary" aria-label="Directory summary">
          <span><strong>80+</strong> Industry terms</span>
          <span><strong>A–Z</strong> Quick reference</span>
          <span><strong>Free</strong> Learning resource</span>
        </div>
      </header>

      <section className="terminology-directory">
        <div className="terminology-section-heading">
          <div>
            <p className="terminology-section-kicker">Terminology directory</p>
            <h2>Gaming terms from A to Z</h2>
          </div>
          <p>
            Browse the reference cards below to learn about common games,
            equipment, formats, and industry language.
          </p>
        </div>
        <div className="flex-container">
        <div className="flex-item">
  <h3>Admission Packet</h3>
  <p>
    The minimum set of cards or paper packs a player must purchase to participate in a bingo game. Typically includes 6-12 cards for regular or special games, with content and price varying by bingo hall.
  </p>
</div>
<div className="flex-item">
  <h3>Auditrack™</h3>
  <p>
    A system that allows game operators to track how many sheets each seller has been assigned and how many have been sold.
  </p>
</div>
<div className="flex-item">
  <h3>Basic</h3>
  <p>
    A simple version of a pull tab game. Players win by matching symbols on the ticket or by revealing prizes under perforated windows on a seal card.
  </p>
</div>
<div className="flex-item">
  <h3>Bingo</h3>
  <p>
    A game of chance where players use cards or sheets with numbers arranged in a grid. Numbers are called randomly, and players aim to match a specific pattern to win.
  </p>
</div>
<div className="flex-item">
  <h3>Bingo Balls</h3>
  <p>
    Small balls, originally wooden but now plastic, used in bingo games. They are mixed in a blower chamber and drawn randomly by the caller.
  </p>
</div>
<div className="flex-item">
  <h3>Bingo Console/Machine</h3>
  <p>
    A device used to operate bingo games, consisting of a blower chamber, a master board, and a ball tray. It mixes and dispenses bingo balls for the caller.
  </p>
</div>
<div className="flex-item">
  <h3>Bingo Event Game</h3>
  <p>
    A pull tab game where certain prizes are determined by drawing bingo balls. Often played alongside regular bingo games.
  </p>
</div>
<div className="flex-item">
  <h3>Bingo Paper Pack or Bingo Booklet</h3>
  <p>
    A collection of bingo sheets, each containing a set number of faces, collated in the order of play for a bingo session.
  </p>
</div>
<div className="flex-item">
  <h3>Blower</h3>
  <p>
    A device that uses forced air to mix bingo balls and dispense them to the caller for announcement.
  </p>
</div>
<div className="flex-item">
  <h3>Bonanza Bingo</h3>
  <p>
    A progressive jackpot game played as the 13th game of a session. Players mark numbers on special cards, with the jackpot increasing weekly until won.
  </p>
</div>
<div className="flex-item">
  <h3>Book</h3>
  <p>
    Two or more sheets glued together, often used for bingo games. These can be straight goods or collations.
  </p>
</div>
<div className="flex-item">
  <h3>Break Open</h3>
  <p>
    A multi-ply card with perforated tabs that reveal symbols or numbers to determine winners. Also known as pull tabs or instant bingo tickets.
  </p>
</div>
<div className="flex-item">
  <h3>Buy-In</h3>
  <p>
    The price a player pays for a packet of bingo paper to participate in a session. Different buy-in levels may include early birds, regular sessions, and specials.
  </p>
</div>
<div className="flex-item">
  <h3>Caller</h3>
  <p>
    The person who announces the randomly drawn bingo numbers during the game.
  </p>
</div>
<div className="flex-item">
  <h3>Card/Face</h3>
  <p>
    A bingo card with 24 numbered spaces and one free space, arranged in a 5x5 grid. Numbers are assigned randomly within specific ranges for each column.
  </p>
</div>
<div className="flex-item">
  <h3>Card Minding Device</h3>
  <p>
    An electronic or mechanical device that allows players to store, display, and mark bingo cards during gameplay.
  </p>
</div>
<div className="flex-item">
  <h3>Carryover</h3>
  <p>
    A game style where unclaimed prize money from one game is carried over to the next game.
  </p>
</div>
<div className="flex-item">
  <h3>Cashboard/Pickboard</h3>
  <p>
    A deal of tickets with instant winners and additional elements of play, such as U-Pick tickets or seal cards, offering multiple ways to win.
  </p>
</div>
<div className="flex-item">
  <h3>Cashboard Multi-Pack</h3>
  <p>
    Several small-count games tied to an oversized seal card. Each deal contributes to a prize pool, allowing for larger prizes than a single deal could provide.
  </p>
</div>
<div className="flex-item">
  <h3>Cashboard Subsets</h3>
  <p>
    A set of tickets broken into smaller deals with the same serial number. Each deal contributes to a prize pool on an oversized seal card, offering larger prizes.
  </p>
</div>
<div className="flex-item">
  <h3>Charitable Organization</h3>
  <p>
    A non-profit organization, such as a religious or educational group, that is exempt from federal income tax under section 501(c) of the Internal Revenue Code.
  </p>
</div>
<div className="flex-item">
  <h3>Chips</h3>
  <p>
    Small plastic discs used to cover called numbers on bingo cards. Magnetic wands are often used to clear the cards after a game.
  </p>
</div>
<div className="flex-item">
  <h3>Coin Board/Merchandise Board</h3>
  <p>
    A game board containing coins or merchandise as prizes, played in conjunction with pull tab tickets. Certain tickets qualify players to win prizes from the board.
  </p>
</div>
<div className="flex-item">
  <h3>Coinboard</h3>
  <p>
    A game board with coins or merchandise as prizes. Players may win rare coins or items like jackets by selecting a window on the board.
  </p>
</div>
<div className="flex-item">
  <h3>Coinboard Overlay</h3>
  <p>
    A graphic placed over a coinboard template to change prize values and game names. This allows for variety using the same board.
  </p>
</div>
<div className="flex-item">
  <h3>Collations</h3>
  <p>
    A set of bingo sheets arranged in a specific color sequence, making it easier for operators to differentiate games during a session.
  </p>
</div>
<div className="flex-item">
  <h3>Common Serial Number</h3>
  <p>
    A unique number printed on bingo faces to identify a set and prevent unauthorized paper from entering the game.
  </p>
</div>
<div className="flex-item">
  <h3>Consolation Prize</h3>
  <p>
    A smaller prize awarded if the main jackpot is not won within a specified number of calls.
  </p>
</div>
<div className="flex-item">
  <h3>Cover-All/Blackout</h3>
  <p>
    A bingo game where players must cover all 24 numbers on their card to win. Often played as a jackpot game.
  </p>
</div>
<div className="flex-item">
  <h3>Cut</h3>
  <p>
    Refers to how a sheet of bingo faces is divided. For example, a 3 ON Vertical cut arranges three faces top-to-bottom, while a Horizontal cut arranges them side-by-side.
  </p>
</div>
<div className="flex-item">
  <h3>Dabber/Dauber</h3>
  <p>
    An ink-filled marker with a foam tip used to mark called numbers on bingo cards. Also known as dabbers or ink markers.
  </p>
</div>
        <div className="flex-item">
  <h3>Deal/Box</h3>
  <p>
    Small boxes inside a case of pull tabs. Each deal represents a separate game or series of pull tabs with the same serial number. Cases may contain two or more deals, depending on the number of tickets.
  </p>
</div>
<div className="flex-item">
  <h3>Diecut</h3>
  <p>
    A process used to create uniquely shaped tickets, such as snowmen, footballs, or keys, adding variety to pull tab games.
  </p>
</div>
<div className="flex-item">
  <h3>Early Bird Game</h3>
  <p>
    A short bingo session of 2-8 games held before the main session. It encourages players to arrive early and typically offers small prizes.
  </p>
</div>
<div className="flex-item">
  <h3>Face</h3>
  <p>
    An individual bingo sheet containing 24 numbers and a free space in the center. Players use these to play bingo games.
  </p>
</div>
<div className="flex-item">
  <h3>Flare Card</h3>
  <p>
    A card or poster included with pull tab deals. It displays the game's name, form number, serial number, ticket cost, and prize details.
  </p>
</div>
<div className="flex-item">
  <h3>Flashboard</h3>
  <p>
    An electronic board in bingo halls that displays the 75 numbers in a game. Numbers light up as they are called.
  </p>
</div>
<div className="flex-item">
  <h3>Floor-Worker</h3>
  <p>
    A staff member responsible for selling pull tabs, verifying winning bingo cards, and ensuring smooth operations during the game.
  </p>
</div>
<div className="flex-item">
  <h3>Free Space</h3>
  <p>
    The center square on a bingo card, which is automatically marked and counts toward any winning pattern.
  </p>
</div>
<div className="flex-item">
  <h3>Free Space Number</h3>
  <p>
    A unique number assigned to the free space on bingo cards, used for verification in checkbooks or electronic systems.
  </p>
</div>
<div className="flex-item">
  <h3>Game Pattern</h3>
  <p>
    The specific arrangement of numbers players must cover to win a bingo game. Examples include diagonal lines, the letter "X," or blackout.
  </p>
</div>
<div className="flex-item">
  <h3>Hard Card</h3>
  <p>
    A reusable bingo card made of durable material, often used with plastic chips to mark numbers.
  </p>
</div>
<div className="flex-item">
  <h3>Hard-Way Bingo</h3>
  <p>
    A bingo pattern requiring a straight line without using the free space in the center of the card.
  </p>
</div>
<div className="flex-item">
  <h3>Inset</h3>
  <p>
    The reverse side of a pull tab ticket that reveals symbol combinations when the tabs or windows are opened.
  </p>
</div>
<div className="flex-item">
  <h3>Instant</h3>
  <p>
    A game of chance similar to pull tabs, where winners are paid immediately without the use of a seal card.
  </p>
</div>
<div className="flex-item">
  <h3>Jackpot</h3>
  <p>
    A large prize awarded for achieving a challenging bingo pattern, such as a blackout, within a specified number of calls.
  </p>
</div>
<div className="flex-item">
  <h3>Jar Ticket</h3>
  <p>
    A lightweight gaming ticket, folded and sealed with colored tape, containing symbols or numbers. Often sold in see-through bins.
  </p>
</div>
<div className="flex-item">
  <h3>Jartab</h3>
  <p>
    A small, one-window pull tab, also known as a Stamp, Rascal, Popp-ette™, or Tabbies.
  </p>
</div>
<div className="flex-item">
  <h3>Late Bird/Nite Owl</h3>
  <p>
    A short bingo session of 2-8 games held after the main session. Small prizes are typically awarded.
  </p>
</div>
<div className="flex-item">
  <h3>Loose</h3>
  <p>
    Jar tickets that are not bundled or stapled together, often sold individually.
  </p>
</div>
<div className="flex-item">
  <h3>Loose Paper</h3>
  <p>
    Bingo paper sold individually rather than collated into books or pads. Typically used for special games.
  </p>
</div>
<div className="flex-item">
  <h3>Lucky Jar/Cookie Jar</h3>
  <p>
    A container holding cash that players can win by bingoing on a lucky number. The lucky number is usually the first number called in a session.
  </p>
</div>
<div className="flex-item">
  <h3>Matching Serial Number</h3>
  <p>
    Indicates that all sections of a bingo paper set share the same serial number, ensuring authenticity.
  </p>
</div>
<div className="flex-item">
  <h3>Minimum Buy-In</h3>
  <p>
    The minimum amount a player must spend to participate in a bingo game.
  </p>
</div>
<div className="flex-item">
  <h3>Money Ball</h3>
  <p>
    A number drawn before the game that doubles a player's winnings if they bingo on that number.
  </p>
</div>
<div className="flex-item">
  <h3>Moonlight Bingo</h3>
  <p>
    A late-night bingo session, typically starting around 10:00 PM, offering a unique gaming experience.
  </p>
</div>
        <div className="flex-item">
  <h3>Multiple Winners</h3>
  <p>
    Occurs when two or more players call bingo at the same time. The prize is divided equally among the winners. For example, if five players win a $500 game, each receives $100.
  </p>
</div>
<div className="flex-item">
  <h3>NAFTM Seal</h3>
  <p>
    A seal mandated by the National Association of Fundraising Ticket Manufacturers (NAFTM) on pull tab deals. It ensures the pull tab is approved and untampered. A broken seal indicates possible tampering.
  </p>
</div>
<div className="flex-item">
  <h3>Nicknames</h3>
  <p>
    Humorous or memorable names used to announce bingo numbers, helping players confirm the call in noisy environments. Examples include "77 – Sunset Strip" and "29 – You're doing fine."
  </p>
</div>
<div className="flex-item">
  <h3>Nite Owl</h3>
  <p>
    A short bingo session of 2-8 games played after the regular session. Small prizes are typically awarded.
  </p>
</div>
<div className="flex-item">
  <h3>Off Cut</h3>
  <p>
    Refers to leftover bingo faces that cannot be cut into the desired ON configuration from the master sheet. For example, a 24 ON Vertical sheet cut into 9 ON sheets leaves six faces as an off cut.
  </p>
</div>
<div className="flex-item">
  <h3>Ons</h3>
  <p>
    The number of bingo faces printed on a single sheet. For example, a 9 ON sheet contains nine bingo faces.
  </p>
</div>
<div className="flex-item">
  <h3>Packets</h3>
  <p>
    A collection of bingo paper "ups" or pages used to attract players to the bingo hall. Often referred to as a "package deal."
  </p>
</div>
<div className="flex-item">
  <h3>Padded</h3>
  <p>
    Bingo paper collated into pads, typically in books of 25. Padded paper provides better control for sellers and is sold in one color per series.
  </p>
</div>
<div className="flex-item">
  <h3>Pattern</h3>
  <p>
    The shape or arrangement of numbers that players must cover to win a bingo game. Common patterns include straight lines, diagonals, and blackout (coverall).
  </p>
</div>
<div className="flex-item">
  <h3>Payout</h3>
  <p>
    The percentage of sales returned to players as prizes. For example, bingo payouts are often higher than state lottery games, which average around 45%.
  </p>
</div>
<div className="flex-item">
  <h3>Percentage Game</h3>
  <p>
    A standalone bingo game where proceeds are split between the winner and the house, typically 50-50 or 60-40.
  </p>
</div>
<div className="flex-item">
  <h3>Popp-ettes™/Rock-ettes™</h3>
  <p>
    Small, one-window pull tabs, also known as Stamps, Jartabs, Rascals, or Tabbies.
  </p>
</div>
<div className="flex-item">
  <h3>Postage Stamp Pattern</h3>
  <p>
    A bingo pattern requiring players to match four numbers in a 2x2 grid, resembling a postage stamp, in any corner of the card.
  </p>
</div>
<div className="flex-item">
  <h3>Progressive Game</h3>
  <p>
    A seal card game with a jackpot that increases over time if not won. The jackpot is carried forward to subsequent deals until claimed.
  </p>
</div>
<div className="flex-item">
  <h3>Progressive Jackpot</h3>
  <p>
    A jackpot that grows daily, weekly, or monthly until won. If not claimed within a set number of calls, a consolation prize is awarded.
  </p>
</div>
<div className="flex-item">
  <h3>Pull Tab/Popp-Open®</h3>
  <p>
    A game of chance using a folded or perforated ticket that conceals symbols or numbers. Players open the tabs to reveal if they have won.
  </p>
</div>
<div className="flex-item">
  <h3>Pushout</h3>
  <p>
    A bingo face printed on heavy paper stock with die-cuts, allowing players to fold numbers when called. Pushouts are reusable.
  </p>
</div>
<div className="flex-item">
  <h3>Quickie</h3>
  <p>
    Also known as Lightning or Speed Bingo, this is a fast-paced game played early in the session with small prizes awarded.
  </p>
</div>
<div className="flex-item">
  <h3>Raffle</h3>
  <p>
    A game of chance where participants purchase tickets for a chance to win a prize, determined by a random drawing.
  </p>
</div>
<div className="flex-item">
  <h3>Rascal</h3>
  <p>
    A small, one-window pull tab, also known as a Stamp, Jartab, Popp-ette™, or Tabbies.
  </p>
</div>
<div className="flex-item">
  <h3>Regular Session</h3>
  <p>
    The main bingo session consisting of 10-20 games, often culminating in a big jackpot game to retain players until the end.
  </p>
</div>
<div className="flex-item">
  <h3>Seal Card</h3>
  <p>
    A heavy paper card used in pull tab games. It contains seals that, when opened, reveal predesignated winning numbers, letters, or symbols.
  </p>
</div>
<div className="flex-item">
  <h3>Serial Number</h3>
  <p>
    A unique number printed on the "N" of each bingo face to identify the set and prevent unauthorized paper from entering the game.
  </p>
</div>
<div className="flex-item">
  <h3>Series</h3>
  <p>
    Indicates the number of unique bingo faces in a set. For example, a 9,000 SERIES contains 9,000 unique faces.
  </p>
</div>
<div className="flex-item">
  <h3>Session</h3>
  <p>
    A complete bingo program consisting of regular and special games, typically lasting 2-3 hours.
  </p>
</div>
<div className="flex-item">
  <h3>Set</h3>
  <p>
    Refers to the number of unique bingo faces in a set of paper, numbered sequentially and in uniform color.
  </p>
</div>
<div className="flex-item">
  <h3>Six-Pack, Nine-Pack</h3>
  <p>
    A block of six or nine numbers on a bingo card. Players must cover all numbers in the block to win.
  </p>
</div>
<div className="flex-item">
  <h3>Slip Sheet</h3>
  <p>
    A wax-coated sheet used to divide collated bingo paper into books after gluing.
  </p>
</div>
        <div className="flex-item">
  <h3>Speed Bingo</h3>
  <p>
    A fast-paced variation of bingo where numbers are called rapidly, and players can win in as few as three numbers. Typically played before or after a regular session.
  </p>
</div>
<div className="flex-item">
  <h3>Stamp</h3>
  <p>
    A one-window pull tab, also referred to as Popp-Ettes™, Rascals, Jartabs, or Tabbies.
  </p>
</div>
<div className="flex-item">
  <h3>Stapled</h3>
  <p>
    A term used for jar tickets when 3, 4, or 5 tickets are stapled together for distribution.
  </p>
</div>
<div className="flex-item">
  <h3>Straight Goods</h3>
  <p>
    Bingo paper cut to specific dimensions, typically sold in one color per case. It can be either loose or padded.
  </p>
</div>
<div className="flex-item">
  <h3>Tabbies</h3>
  <p>
    Small, one-window pull tabs, also known as Stamps, Jartabs, Rascals, or Popp-Ettes™.
  </p>
</div>
<div className="flex-item">
  <h3>Tear-Opens™</h3>
  <p>
    Also known as Bonanza™, these are single bingo faces that are folded and sealed, concealing the face until sold and "torn open." Often used for large jackpot games.
  </p>
</div>
<div className="flex-item">
  <h3>Up</h3>
  <p>
    Refers to the number of sheets or pages in a bingo book. For example, a 10 UP book contains ten sheets of bingo paper.
  </p>
</div>
<div className="flex-item">
  <h3>U-PIK-EM™</h3>
  <p>
    A two-part NCR form allowing players to select their numbers for a special bingo game. The top copy is retained by the player, while the second copy is submitted for verification.
  </p>
</div>
<div className="flex-item">
  <h3>Validation</h3>
  <p>
    A requirement for eligibility to win additional jackpot amounts. The price varies based on the number of cards played.
  </p>
</div>
<div className="flex-item">
  <h3>Warm Up</h3>
  <p>
    A short bingo session consisting of 2-4 games, designed to get players ready for the main session. Small prizes are typically awarded.
  </p>
</div>
<div className="flex-item">
  <h3>Wild Number</h3>
  <p>
    A number drawn before the game that determines additional numbers to be marked. For example, if N42 is drawn, all numbers ending in 2 are marked.
  </p>
</div>
<div className="flex-item">
  <h3>Winner Takes All</h3>
  <p>
    A standalone bingo game where all money collected from card sales is awarded as the prize.
  </p>
</div>
<div className="flex-item">
  <h3>Wrap Up</h3>
  <p>
    The final game of a bingo session, often marking the conclusion of the event.
  </p>
</div>
        </div>
      </section>
    </main>
  );
};

export default GameTerminology;
